import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogContent,
  Drawer,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DirectionsCarRoundedIcon from '@mui/icons-material/DirectionsCarRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import SmsRoundedIcon from '@mui/icons-material/SmsRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import PageHeader from '../components/PageHeader';
import DataState from '../components/DataState';
import PaymentStatusBadge from '../components/PaymentStatusBadge';
import PaymentFailedSmsPreview from '../components/PaymentFailedSmsPreview';
import {
  fetchOwnerMemberContact,
  fetchOwnerMemberDetail,
  fetchOwnerMembers,
} from '../api/members';
import { useSession } from '../permissions/SessionContext';
import { hasPermissions } from '../permissions/check';
import { colors, layout, radius, shadows } from '../design-system/tokens';
import { downloadCsv } from '../utils/csv';

// 빠른 필터 유효 키 — URL ?filter= 로 들어온 값 검증에도 쓴다
const FILTER_KEYS = ['all', 'active', 'cancelling', 'payment_failed', 'inactive30', 'no_vehicle'];
/** CSV 내보내기 상한 — 마스킹 값만 담지만 과도한 조회를 막는다 */
const CSV_MAX_ROWS = 500;

const formatNumber = (value) => Number(value || 0).toLocaleString('ko-KR');
const formatWon = (value) => (value == null ? '-' : `${formatNumber(value)}원`);

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('ko-KR', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const formatDateOnly = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
};

const statusStyle = (status) => {
  if (status === 'active' || status === 'ACTIVE') return colors.status.success;
  if (status === 'DELETED' || status === 'deleted') return colors.status.danger;
  return colors.status.neutral;
};

const MEMBER_STATUS_LABELS = {
  active: '이용 중',
  ACTIVE: '이용 중',
  inactive: '비활성',
  INACTIVE: '비활성',
  suspended: '정지',
  SUSPENDED: '정지',
  deleted: '탈퇴',
  DELETED: '탈퇴',
};

const SUBSCRIPTION_STATUS_LABELS = {
  active: '이용 중',
  cancellation_requested: '해지 예약',
  cancelled: '해지 완료',
  expired: '만료',
  failed: '결제 실패',
  pending: '대기',
};

const getMemberStatusLabel = (status) => MEMBER_STATUS_LABELS[status] || status || '-';
const getSubscriptionStatusLabel = (subscription) =>
  subscription?.statusLabel || SUBSCRIPTION_STATUS_LABELS[subscription?.status] || '상태 확인';

const subscriptionStyle = (status) => {
  if (status === 'active') return colors.status.success;
  if (status === 'cancellation_requested') return colors.status.warning;
  return colors.status.neutral;
};

/** 다음 액션 severity → 색 토큰 */
const actionStyle = (severity) => {
  if (severity === 'danger') return colors.status.danger;
  if (severity === 'warning') return colors.status.warning;
  return colors.status.info;
};

/**
 * 빠른 필터 — 점주가 매일 확인해야 하는 묶음.
 * countKey 는 summary 응답의 필드명이며, 없으면 건수 뱃지를 표시하지 않는다.
 */
const QUICK_FILTERS = [
  { key: 'all', label: '전체' },
  { key: 'active', label: '이용 중', countKey: 'activeSubscriptions' },
  { key: 'cancelling', label: '해지 예약', countKey: 'cancellationRequested' },
  {
    key: 'payment_failed',
    label: '결제 실패',
    countKey: 'paymentFailed',
    tone: 'danger',
  },
  { key: 'inactive30', label: '30일 미이용', countKey: 'inactive30' },
  { key: 'no_vehicle', label: '정보 미완성', countKey: 'noVehicle' },
];

const getScopedParams = (session) =>
  session?.user?.role === 'master' ? { storeId: session.store.id } : {};

const VehiclesText = ({ vehicles }) => {
  if (!vehicles?.length) {
    return (
      <Typography variant="body2" sx={{ color: colors.text.muted }}>
        -
      </Typography>
    );
  }
  return (
    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
      {vehicles.slice(0, 2).map((vehicle) => (
        <Chip
          key={vehicle.id}
          size="small"
          icon={<DirectionsCarRoundedIcon />}
          label={vehicle.licensePlate}
          sx={{
            bgcolor: colors.status.neutral.bg,
            color: colors.status.neutral.fg,
          }}
        />
      ))}
      {vehicles.length > 2 && (
        <Chip size="small" label={`+${vehicles.length - 2}`} />
      )}
    </Stack>
  );
};

/** 점주가 바로 조치할 항목 — 없으면 아무것도 그리지 않아 표가 조용하게 유지된다 */
const NextActions = ({ actions, dense = false }) => {
  if (!actions?.length) {
    return dense ? (
      <Typography variant="caption" sx={{ color: colors.text.muted }}>
        -
      </Typography>
    ) : null;
  }
  return (
    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
      {actions.map((action) => {
        const tone = actionStyle(action.severity);
        return (
          <Tooltip
            key={action.type}
            title={action.detail || ''}
            arrow
            disableHoverListener={!action.detail}
          >
            <Chip
              size="small"
              label={action.label}
              sx={{
                bgcolor: tone.bg,
                color: tone.fg,
                fontWeight: 700,
                height: 22,
                fontSize: 11,
              }}
            />
          </Tooltip>
        );
      })}
    </Stack>
  );
};

/** 운영 타임라인 — 가입·차량·멤버십·결제·이용을 한 줄씩 시간 역순으로 */
const TIMELINE_TONE = {
  joined: colors.status.info,
  vehicle_registered: colors.status.neutral,
  subscription: colors.status.info,
  wash: colors.status.neutral,
};

const Timeline = ({ entries }) => {
  if (!entries?.length) {
    return (
      <Typography variant="body2" sx={{ color: colors.text.muted }}>
        기록이 없습니다.
      </Typography>
    );
  }

  return (
    <Stack spacing={0}>
      {entries.map((entry, index) => {
        const tone =
          entry.type === 'payment'
            ? entry.outcome === 'failed'
              ? colors.status.danger
              : colors.status.success
            : (TIMELINE_TONE[entry.type] ?? colors.status.neutral);
        const isLast = index === entries.length - 1;

        return (
          <Box
            key={`${entry.type}-${entry.at}-${index}`}
            sx={{ display: 'flex', gap: 1.25 }}
          >
            {/* 점 + 연결선 */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                pt: 0.75,
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: tone.fg,
                  flexShrink: 0,
                }}
              />
              {!isLast && (
                <Box
                  sx={{
                    width: '1px',
                    flexGrow: 1,
                    bgcolor: colors.border.soft,
                    my: 0.5,
                  }}
                />
              )}
            </Box>
            <Box sx={{ pb: isLast ? 0 : 1.5, minWidth: 0, flexGrow: 1 }}>
              <Stack
                direction="row"
                spacing={1}
                sx={{ alignItems: 'baseline', flexWrap: 'wrap', rowGap: 0.25 }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {entry.title}
                </Typography>
                <Typography variant="caption" sx={{ color: colors.text.muted }}>
                  {formatDate(entry.at)}
                </Typography>
              </Stack>
              {entry.detail && (
                <Typography variant="caption" sx={{ color: colors.text.muted }}>
                  {entry.detail}
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
};

const InfoBlock = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography
      variant="caption"
      sx={{ color: colors.text.muted, fontWeight: 700 }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{ mt: 0.5, fontWeight: 700, wordBreak: 'keep-all', overflowWrap: 'anywhere' }}
    >
      {value}
    </Typography>
  </Box>
);

const SectionTitle = ({ title, hint }) => (
  <Stack
    direction="row"
    spacing={1}
    sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}
  >
    <Typography
      variant="subtitle2"
      sx={{ fontWeight: 800, color: colors.text.primary }}
    >
      {title}
    </Typography>
    {hint && (
      <Typography variant="caption" sx={{ color: colors.text.muted }}>
        {hint}
      </Typography>
    )}
  </Stack>
);

const MemberDetail = ({
  detail,
  loading,
  error,
  contact,
  contactLoading,
  contactError,
  onRevealContact,
  onClose,
  canNotify,
  masterQaMode,
}) => {
  const member = detail?.member;
  const [smsOpen, setSmsOpen] = useState(false);
  const [repayOpen, setRepayOpen] = useState(false);
  const hasPaymentFailure =
    member?.nextActions?.some((action) => action.type === 'payment_failed') ||
    member?.subscriptions?.some((subscription) => Number(subscription.retryCount || 0) > 0);
  const failedSubscription =
    member?.subscriptions?.find((subscription) => Number(subscription.retryCount || 0) > 0) ||
    member?.subscriptions?.[0];
  const smsMessage = member
    ? `[${detail?.scope?.store?.name || '모구'}]\n${member.name}님, 월 구독 결제(${formatWon(
        failedSubscription?.effectivePrice,
      )})가 완료되지 않았어요. 카드 확인 후 다시 시도해 주세요. 문의 0507-1234-5678`
    : '';

  return (
    <>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          bgcolor: colors.surface,
          px: 2,
          pr: 6,
          py: 1.5,
          borderBottom: `1px solid ${colors.border.soft}`,
        }}
      >
        <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center', minWidth: 0 }}>
          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: colors.brand.primarySoft,
              color: colors.brand.primary,
              fontSize: 14,
              fontWeight: 800,
            }}
          >
            {member?.name?.slice(0, 1) || '회'}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5 }}>
              <Typography variant="subtitle2" sx={{ fontSize: 15, minWidth: 0 }}>
                {member ? member.name : '회원 상세'}
              </Typography>
              {hasPaymentFailure && <PaymentStatusBadge label="결제 실패" status="failed" />}
            </Stack>
            <Typography variant="caption" sx={{ color: colors.text.muted, display: 'block' }}>
              {member ? `${formatDateOnly(member.joinedAt)} 가입 · ${member.activeSubscription?.productName || '멤버십 없음'}` : '불러오는 중'}
            </Typography>
          </Box>
        </Stack>
        <IconButton
          onClick={onClose}
          aria-label="닫기"
          sx={{ position: 'absolute', right: 12, top: 12 }}
        >
          <CloseRoundedIcon />
        </IconButton>
      </Box>
      <DialogContent
        dividers
        sx={{
          p: 2,
          borderTop: 0,
          bgcolor: colors.surface,
        }}
      >
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={26} />
          </Box>
        )}

        {!loading && error && (
          <Alert severity="error">
            {error}
          </Alert>
        )}

        {!loading && !error && member && (
          <Stack spacing={2.25}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                gap: 1,
              }}
            >
              <Paper sx={{ p: 1.5, bgcolor: colors.surface }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  sx={{ justifyContent: 'space-between', gap: 1, minWidth: 0 }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <InfoBlock
                      label="연락처"
                      value={
                        contact?.phone
                          ? `${contact.phone}${contact.phoneVerified ? ' (인증)' : ''}`
                          : `${member.phoneMasked || '-'}${member.phoneVerified ? ' (인증)' : ''}`
                      }
                    />
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', mt: 0.5, color: colors.text.muted, wordBreak: 'break-all' }}
                    >
                      {contact?.email || member.emailMasked || '-'}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<VisibilityRoundedIcon fontSize="small" />}
                    onClick={onRevealContact}
                    disabled={contactLoading || Boolean(contact)}
                    sx={{ alignSelf: 'flex-start', flexShrink: 0, px: 0.5 }}
                  >
                    {contact
                      ? '연락처 확인됨'
                      : contactLoading
                        ? '불러오는 중'
                        : '연락처 보기'}
                  </Button>
                </Stack>
                <Typography variant="caption" sx={{ color: colors.text.muted }}>
                  전체 번호를 열람하면 누가 언제 봤는지 열람 기록이 남습니다.
                </Typography>
                {contactError && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {contactError}
                  </Alert>
                )}
              </Paper>
              <Paper sx={{ p: 1.5, bgcolor: colors.surface }}>
                <InfoBlock
                  label="차량"
                  value={
                    member.vehicles?.length
                      ? member.vehicles
                          .map((vehicle) => `${vehicle.licensePlate}${vehicle.isPrimary ? ' · 대표' : ''}`)
                          .join(', ')
                      : '등록된 차량 없음'
                  }
                />
                <Typography variant="caption" sx={{ color: colors.text.muted }}>
                  LPR 인식 기준 차량 정보예요.
                </Typography>
              </Paper>
            </Box>

            {hasPaymentFailure && (
              <Paper
                sx={{
                  p: 2,
                  bgcolor: colors.status.danger.bg,
                  borderColor: colors.status.danger.border,
                }}
              >
                <Typography variant="subtitle2" sx={{ color: colors.status.danger.fg }}>
                  7월 20일 정기 결제에 실패했어요
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, color: colors.text.secondary }}>
                  카드 한도 초과 · {failedSubscription?.productName || '월 멤버십'}{' '}
                  {formatWon(failedSubscription?.effectivePrice)} · {failedSubscription?.retryCount || 2}회 시도함
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1.25, flexWrap: 'wrap', rowGap: 1 }}>
                  {canNotify && (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<SmsRoundedIcon />}
                      onClick={() => {
                        setSmsOpen(true);
                        setRepayOpen(false);
                      }}
                    >
                      안내 문자 미리보기
                    </Button>
                  )}
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<ReplayRoundedIcon />}
                    onClick={() => {
                      setRepayOpen(true);
                      setSmsOpen(false);
                      }}
                      sx={{ color: colors.status.danger.fg, borderColor: colors.status.danger.border }}
                    >
                    재결제 확인
                  </Button>
                </Stack>
              </Paper>
            )}

            {smsOpen && (
              <Paper sx={{ overflow: 'hidden', borderColor: colors.brand.primarySoftBorder }}>
                <Box sx={{ px: 1.75, py: 1.25, bgcolor: colors.brand.primarySoft }}>
                  <Typography variant="subtitle2" sx={{ color: colors.brand.primary }}>
                    보내기 전에 내용을 확인하세요
                  </Typography>
                </Box>
                <Box sx={{ p: 2 }}>
                  <Box
                    sx={{
                      maxWidth: 320,
                      p: 1.5,
                      bgcolor: colors.status.neutral.bg,
                      borderRadius: '8px',
                      borderTopLeftRadius: '2px',
                      whiteSpace: 'pre-wrap',
                      color: colors.text.primary,
                      fontSize: 13,
                      lineHeight: 1.65,
                    }}
                  >
                    {smsMessage}
                  </Box>
                  <Typography variant="caption" sx={{ display: 'block', mt: 1, color: colors.text.muted }}>
                    LMS 1건 · {member.phoneMasked || '연락처 없음'} · 발송 즉시 기록이 남아요.
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1.25, flexWrap: 'wrap', rowGap: 1 }}>
                    <Button variant="contained" size="small" disabled>
                      {masterQaMode ? 'Master QA 발송 차단' : '발송 준비 중'}
                    </Button>
                    <Button size="small" onClick={() => setSmsOpen(false)}>
                      닫기
                    </Button>
                  </Stack>
                  {masterQaMode && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: colors.qa.fg }}>
                      Master QA에서는 실제로 발송되지 않아요.
                    </Typography>
                  )}
                </Box>
              </Paper>
            )}

            {repayOpen && (
              <Paper sx={{ overflow: 'hidden', borderColor: colors.status.danger.border }}>
                <Box sx={{ px: 1.75, py: 1.25, bgcolor: colors.status.danger.bg }}>
                  <Typography variant="subtitle2" sx={{ color: colors.status.danger.fg }}>
                    실행 전 마지막 확인이에요
                  </Typography>
                </Box>
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                    {member.name}님의 {failedSubscription?.productName || '월 멤버십'}에{' '}
                    <b>{formatWon(failedSubscription?.effectivePrice)}</b> 재결제를 시도합니다.
                    목록에서 바로 실행하지 않고 이 확인 패널을 반드시 거쳐요.
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1.25, flexWrap: 'wrap', rowGap: 1 }}>
                    <Button
                      variant="contained"
                      size="small"
                      disabled
                      sx={{ bgcolor: colors.status.danger.solid }}
                    >
                      {masterQaMode
                        ? 'Master QA 실행 차단'
                        : '재결제 실행 준비 중'}
                    </Button>
                    <Button size="small" onClick={() => setRepayOpen(false)}>
                      지금은 하지 않기
                    </Button>
                  </Stack>
                </Box>
              </Paper>
            )}

            <Box>
              <SectionTitle title="차량" />
              <Box sx={{ mt: 1 }}>
                {member.vehicles?.length ? (
                  <Stack
                    direction="row"
                    spacing={0.75}
                    sx={{ flexWrap: 'wrap', rowGap: 0.75 }}
                  >
                    {member.vehicles.map((vehicle) => (
                      <Chip
                        key={vehicle.id}
                        size="small"
                        icon={<DirectionsCarRoundedIcon />}
                        label={`${vehicle.licensePlate}${vehicle.isPrimary ? ' · 대표' : ''}`}
                      />
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" sx={{ color: colors.text.muted }}>
                    등록된 차량이 없습니다.
                  </Typography>
                )}
              </Box>
            </Box>

            <Box>
              <SectionTitle title="멤버십" />
              <Stack spacing={1} sx={{ mt: 1 }}>
                {member.subscriptions?.length ? (
                  member.subscriptions.map((subscription) => {
                    const tone = subscriptionStyle(subscription.status);
                    return (
                      <Paper key={subscription.id} sx={{ p: 2 }}>
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1}
                          sx={{
                            justifyContent: 'space-between',
                            alignItems: { xs: 'flex-start', sm: 'center' },
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700 }}
                            >
                              {subscription.productName}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: colors.text.muted }}
                            >
                              {subscription.vehicleLicensePlate ||
                                '차량 미지정'}{' '}
                              · {formatWon(subscription.effectivePrice)}
                            </Typography>
                          </Box>
                          <Stack
                            direction="row"
                            spacing={1}
                            sx={{ alignItems: 'center' }}
                          >
                            {subscription.retryCount > 0 && (
                              <Chip
                                size="small"
                                label={`결제 재시도 ${subscription.retryCount}회`}
                                sx={{
                                  bgcolor: colors.status.danger.bg,
                                  color: colors.status.danger.fg,
                                  fontWeight: 700,
                                }}
                              />
                            )}
                            <Chip
                              size="small"
                              label={subscription.statusLabel}
                              sx={{
                                bgcolor: tone.bg,
                                color: tone.fg,
                                fontWeight: 700,
                              }}
                            />
                          </Stack>
                        </Stack>
                        <Typography
                          variant="caption"
                          sx={{
                            color: colors.text.muted,
                            display: 'block',
                            mt: 1,
                          }}
                        >
                          다음 결제{' '}
                          {formatDateOnly(subscription.nextPaymentDate)} · 만료{' '}
                          {formatDateOnly(subscription.expirationDate)}
                        </Typography>
                      </Paper>
                    );
                  })
                ) : (
                  <Typography variant="body2" sx={{ color: colors.text.muted }}>
                    멤버십 이력이 없습니다.
                  </Typography>
                )}
              </Stack>
            </Box>

            <Box>
              <SectionTitle title="운영 타임라인" hint="최근 순" />
              <Box sx={{ mt: 1.25 }}>
                <Timeline entries={member.timeline} />
              </Box>
            </Box>

            {member.nextActions?.length > 0 && (
              <Box>
                <SectionTitle title="다음 액션" />
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {member.nextActions.map((action) => {
                    const tone = actionStyle(action.severity);
                    return (
                      <Paper
                        key={action.type}
                        sx={{
                          p: 1.25,
                          display: 'flex',
                          gap: 1,
                          alignItems: 'center',
                          bgcolor: colors.surface,
                        }}
                      >
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            bgcolor: tone.fg,
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="body2">{action.detail || action.label}</Typography>
                      </Paper>
                    );
                  })}
                </Stack>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
    </>
  );
};

const Members = () => {
  const { session, masterQaMode } = useSession();
  const isMobile = useMediaQuery(`(max-width:${layout.mobileBreakpoint - 1}px)`);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [scope, setScope] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(20);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // 대시보드에서 넘어오는 딥링크 ?filter= 를 초기값으로 받는다
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState(() => {
    const initial = searchParams.get('filter');
    return FILTER_KEYS.includes(initial) ? initial : 'all';
  });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [contact, setContact] = useState(null);
  const [contactLoading, setContactLoading] = useState(false);
  const [contactError, setContactError] = useState('');

  const scopedParams = useMemo(() => getScopedParams(session), [session]);
  const storeId = session.store.id;
  // member.notify 권한자만 안내 문자 액션 surface 에 접근 (staff/readonly 불가)
  const canNotify = hasPermissions(session, ['member.notify']);
  const [smsPreviewOpen, setSmsPreviewOpen] = useState(false);

  // 타이핑마다 API 를 때리지 않도록 300ms 디바운스
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // 검색어·필터·매장이 바뀌면 첫 페이지로. load 보다 먼저 실행돼 중복 호출을 막는다.
  useEffect(() => {
    setPage(0);
  }, [storeId, debouncedSearch, filter]);

  // 필터를 URL ?filter= 에 반영 — 새로고침·공유 시 상태가 유지되고 대시보드 딥링크와 대칭이 된다
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (filter === 'all') next.delete('filter');
        else next.set('filter', filter);
        return next;
      },
      { replace: true },
    );
  }, [filter, setSearchParams]);

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      // 현재 필터·검색 조건으로 전체(상한 내)를 받아 마스킹 값만 CSV 로 낸다.
      // 원문 PII 가 아니므로 감사 대상이 아니다.
      const data = await fetchOwnerMembers({
        ...scopedParams,
        search: debouncedSearch || undefined,
        filter: filter === 'all' ? undefined : filter,
        page: 1,
        limit: CSV_MAX_ROWS,
      });
      const rows = (data.members ?? []).map((m) => ({
        이름: m.name,
        상태: getMemberStatusLabel(m.status),
        연락처: m.phoneMasked ?? '',
        이메일: m.emailMasked ?? '',
        차량: (m.vehicles ?? []).map((v) => v.licensePlate).join(' '),
        멤버십: m.activeSubscription ? getSubscriptionStatusLabel(m.activeSubscription) : '없음',
        상품: m.activeSubscription?.productName ?? '',
        최근이용: m.lastWashAt ? formatDate(m.lastWashAt) : '',
        가입일: formatDateOnly(m.joinedAt),
        확인필요: (m.nextActions ?? []).map((a) => a.label).join(', '),
      }));
      const storeCode = scope?.store?.code || session.store.code || 'store';
      const stamp = new Date().toISOString().slice(0, 10);
      downloadCsv(`members_${storeCode}_${stamp}.csv`, rows);
    } catch (err) {
      setError(err?.response?.data?.message || 'CSV 내보내기에 실패했어요.');
    } finally {
      setExporting(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchOwnerMembers({
        ...scopedParams,
        search: debouncedSearch || undefined,
        filter: filter === 'all' ? undefined : filter,
        page: page + 1,
        limit,
      });
      setRows(data.members ?? []);
      setSummary(data.summary ?? null);
      setScope(data.scope ?? null);
      setTotal(data.total ?? 0);
    } catch (err) {
      setError(
        err?.response?.data?.message || '회원 목록을 불러오지 못했어요.',
      );
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, filter, limit, page, scopedParams]);

  useEffect(() => {
    load();
  }, [load]);

  const openDetail = async (memberId) => {
    setSelectedId(memberId);
    setDetail(null);
    setDetailError('');
    setContact(null);
    setContactError('');
    setDetailLoading(true);
    try {
      const data = await fetchOwnerMemberDetail(memberId, scopedParams);
      setDetail(data);
    } catch (err) {
      setDetailError(
        err?.response?.data?.message || '회원 상세를 불러오지 못했어요.',
      );
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelectedId(null);
    setDetail(null);
    setDetailError('');
    setDetailLoading(false);
    setContact(null);
    setContactError('');
    setContactLoading(false);
  };

  const revealContact = async () => {
    if (!selectedId || contact || contactLoading) return;
    setContactLoading(true);
    setContactError('');
    try {
      const data = await fetchOwnerMemberContact(selectedId, scopedParams);
      setContact(data.contact ?? data);
    } catch (err) {
      setContactError(
        err?.response?.data?.message || '연락처를 불러오지 못했어요.',
      );
    } finally {
      setContactLoading(false);
    }
  };

  // 운영툴이므로 카드를 늘어놓기보다 한 줄 요약으로 압축한다
  const stats = [
    { label: '연결 회원', value: `${formatNumber(summary?.totalMembers)}명` },
    {
      label: '이번 달 신규',
      value: `${formatNumber(summary?.newMembersThisMonth)}명`,
    },
    {
      label: '이용 중',
      value: `${formatNumber(summary?.activeSubscriptions)}건`,
    },
    { label: '등록 차량', value: `${formatNumber(summary?.vehicleCount)}대` },
    {
      label: '30일 이용',
      value: `${formatNumber(summary?.recentWashCount)}건`,
    },
  ];

  return (
    <Box>
      <Stack
        direction="row"
        spacing={2.25}
        sx={{
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          rowGap: 1.25,
        }}
      >
        <PageHeader
          title="회원 관리"
          description={`${scope?.store?.name || session.store.name} 이용 회원이에요. 연락처는 기본적으로 가려서 보여줍니다.`}
        />
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: 'center',
            justifyContent: { xs: 'flex-start', sm: 'flex-end' },
            flexWrap: 'wrap',
            rowGap: 1,
            width: { xs: '100%', md: 'auto' },
          }}
        >
          {canNotify && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<SmsRoundedIcon />}
              onClick={() => setSmsPreviewOpen(true)}
              sx={{ flexShrink: 0, maxWidth: { xs: '100%', sm: 'none' } }}
            >
              결제 실패 안내 문자
            </Button>
          )}
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadRoundedIcon />}
            onClick={handleExportCsv}
            disabled={exporting || loading || rows.length === 0}
            sx={{ flexShrink: 0, maxWidth: { xs: '100%', sm: 'none' } }}
          >
            {exporting ? '내보내는 중…' : 'CSV'}
          </Button>
          <Tooltip title="새로고침" arrow>
            <span>
              <IconButton onClick={load} disabled={loading} aria-label="새로고침">
                <RefreshRoundedIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {scope?.legacyNullStoreIncluded && (
        <Alert severity="warning" sx={{ mb: layout.cardGap }}>
          매장 정보가 없는 초기 데이터를 이 매장 실적으로 함께 집계하고 있어요.
          다른 매장이 늘어나기 전에 데이터 정리가 필요합니다.
        </Alert>
      )}

      {/* 요약 — 한 줄 압축 */}
      <Paper sx={{ mb: layout.cardGap, overflow: 'hidden' }}>
        <Box
          sx={{
            px: { xs: 2, sm: 2.25 },
            py: 1.1,
            bgcolor: colors.canvas.subtle,
            borderBottom: `1px solid ${colors.border.soft}`,
          }}
        >
          <Typography variant="caption" sx={{ color: colors.text.muted, fontWeight: 700 }}>
            운영 요약
          </Typography>
        </Box>
        <Box
          sx={{
            p: { xs: 2, sm: 2.25 },
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 118px), 1fr))',
            gap: layout.cardGap,
          }}
        >
          {stats.map((stat) => (
            <Box key={stat.label} sx={{ minWidth: 0 }}>
              <Typography
                variant="caption"
                sx={{ color: colors.text.muted, fontWeight: 700 }}
              >
                {stat.label}
              </Typography>
              <Typography variant="h6" sx={{ mt: 0.25, fontVariantNumeric: 'tabular-nums' }}>
                {stat.value}
              </Typography>
            </Box>
          ))}
        </Box>
      </Paper>

      {/* 빠른 필터 + 검색 */}
      <Paper sx={{ p: { xs: 2, sm: 2.25 }, mb: layout.cardGap }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.75}
          sx={{
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', md: 'center' },
          }}
        >
          <Stack
            direction="row"
            spacing={0.75}
            sx={{ flexWrap: 'wrap', rowGap: 0.75 }}
            role="group"
            aria-label="빠른 필터"
          >
            {QUICK_FILTERS.map((item) => {
              const selected = filter === item.key;
              const count = item.countKey ? summary?.[item.countKey] : null;
              const danger = item.tone === 'danger' && Number(count) > 0;
              return (
                <Chip
                  key={item.key}
                  label={
                    count == null
                      ? item.label
                      : `${item.label} ${formatNumber(count)}`
                  }
                  onClick={() => setFilter(item.key)}
                  aria-pressed={selected}
                  sx={{
                    height: 28,
                    fontWeight: 700,
                    bgcolor: selected
                      ? colors.text.primary
                      : danger
                        ? colors.status.danger.bg
                        : colors.surface,
                    color: selected
                      ? colors.text.inverse
                      : danger
                        ? colors.status.danger.fg
                        : colors.text.secondary,
                    border: selected
                      ? `1px solid ${colors.text.primary}`
                      : `1px solid ${colors.border.muted}`,
                    '&:hover': {
                      bgcolor: selected
                        ? colors.text.primary
                        : colors.surface,
                      borderColor: selected
                        ? colors.text.primary
                        : colors.brand.primarySoftBorder,
                    },
                  }}
                />
              );
            })}
          </Stack>

          <TextField
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="이름·연락처·차량번호"
            sx={{ minWidth: { xs: '100%', md: 260 } }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon
                      fontSize="small"
                      sx={{ color: colors.text.muted }}
                    />
                  </InputAdornment>
                ),
              },
              htmlInput: { 'aria-label': '회원 검색' },
            }}
          />
        </Stack>
      </Paper>

      <DataState
        loading={loading}
        error={error}
        onRetry={load}
        skeleton={{ rows: 6, cols: 7 }}
        empty={rows.length === 0}
        emptyText={
          debouncedSearch || filter !== 'all'
            ? '조건에 맞는 회원이 없어요.'
            : '아직 이 매장에 연결된 회원이 없어요.'
        }
      >
        <Paper sx={{ p: 0, overflow: 'hidden' }}>
          <TableContainer sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <Table size="small" sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      whiteSpace: 'nowrap',
                      width: 76,
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      bgcolor: colors.surface,
                      boxShadow: `1px 0 0 ${colors.border.soft}`,
                    }}
                  >
                    상세
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>회원</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>연락처</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>차량</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>멤버십</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>최근 이용</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>확인 필요</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const tone = statusStyle(row.status);
                  const subscription = row.activeSubscription;
                  const subTone = subscriptionStyle(subscription?.status);
                  return (
                    <TableRow
                      key={row.id}
                      hover
                      onClick={() => openDetail(row.id)}
                      sx={{
                        cursor: 'pointer',
                      }}
                    >
                      <TableCell
                        sx={{
                          whiteSpace: 'nowrap',
                          position: 'sticky',
                          left: 0,
                          zIndex: 1,
                          bgcolor: colors.surface,
                          boxShadow: `1px 0 0 ${colors.border.soft}`,
                        }}
                      >
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<VisibilityRoundedIcon fontSize="small" />}
                          aria-label={`${row.name} 상세 보기`}
                          onClick={(event) => {
                            event.stopPropagation();
                            openDetail(row.id);
                          }}
                          sx={{ minWidth: 64, px: 1, py: 0.5 }}
                        >
                          상세
                        </Button>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ alignItems: 'center' }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {row.name}
                          </Typography>
                          <Chip
                            size="small"
                            label={getMemberStatusLabel(row.status)}
                            sx={{
                              height: 20,
                              fontSize: 11,
                              bgcolor: tone.bg,
                              color: tone.fg,
                            }}
                          />
                        </Stack>
                        <Typography
                          variant="caption"
                          sx={{ color: colors.text.muted }}
                        >
                          가입 {formatDateOnly(row.joinedAt)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="body2">
                          {row.phoneMasked || '-'}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: colors.text.muted }}
                        >
                          {row.emailMasked || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <VehiclesText vehicles={row.vehicles} />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {subscription ? (
                          <>
                            <Chip
                              size="small"
                              label={getSubscriptionStatusLabel(subscription)}
                              sx={{
                                height: 20,
                                fontSize: 11,
                                fontWeight: 700,
                                bgcolor: subTone.bg,
                                color: subTone.fg,
                              }}
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                color: colors.text.muted,
                                display: 'block',
                                mt: 0.25,
                              }}
                            >
                              {subscription.productName}
                            </Typography>
                          </>
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{ color: colors.text.muted }}
                          >
                            없음
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="body2">
                          {formatDate(row.lastWashAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <NextActions actions={row.nextActions} dense />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(event, next) => setPage(next)}
            rowsPerPage={limit}
            onRowsPerPageChange={(event) => {
              setLimit(Number(event.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50, 100]}
            labelRowsPerPage="페이지당"
          />
        </Paper>
      </DataState>

      <Drawer
        anchor={isMobile ? 'bottom' : 'right'}
        open={Boolean(selectedId)}
        onClose={closeDetail}
        slotProps={{
          root: { keepMounted: true },
          paper: {
            role: 'dialog',
            'aria-label': '회원 상세',
            sx: {
              width: isMobile ? '100%' : 440,
              maxWidth: isMobile ? '100%' : '92vw',
              height: isMobile ? '91vh' : '100vh',
              maxHeight: isMobile ? '91vh' : '100vh',
              borderRadius: isMobile ? `${radius.md}px ${radius.md}px 0 0` : 0,
              borderTop: isMobile ? `1px solid ${colors.border.muted}` : 0,
              borderLeft: isMobile ? 0 : `1px solid ${colors.border.muted}`,
              boxShadow: shadows.drawer,
              overflow: 'hidden',
            },
          },
          backdrop: {
            sx: { bgcolor: shadows.backdrop },
          },
        }}
      >
        <MemberDetail
          detail={detail}
          loading={detailLoading}
          error={detailError}
          contact={contact}
          contactLoading={contactLoading}
          contactError={contactError}
          onRevealContact={revealContact}
          onClose={closeDetail}
          canNotify={canNotify}
          masterQaMode={masterQaMode}
        />
      </Drawer>

      {canNotify && (
        <PaymentFailedSmsPreview
          open={smsPreviewOpen}
          session={session}
          onClose={() => setSmsPreviewOpen(false)}
        />
      )}

      <Box sx={{ height: layout.cardGap }} />
    </Box>
  );
};

export default Members;
